import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaArrowLeft, FaCopy, FaEye, FaEyeSlash, FaSyncAlt, FaTimes } from 'react-icons/fa';
import LoadingSpinner from './LoadingSpinner.jsx';
import ConfirmationModal from './modal/ConfirmationModal.jsx';
import MultistreamFeedIndicator from './MultistreamFeedIndicator.jsx';
import MultistreamInstructions from './MultistreamInstructions.jsx';
import config from '../../config.js';
import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import { isRetryableStreamKeyError, streamKeyErrorKey } from '../../_helpers/multistream.js';
import { useMultistreamStatus } from '../../hooks/useMultistreamStatus.js';
import { useT } from '../../contexts/LocaleContext.jsx';
import '../../styles/partial/Multistream.css';

const MASKED_KEY = '••••••••••••••••';
const COPY_RESET_MS = 2000;

/** Copies `text`, falling back to a hidden textarea where the Clipboard API is refused. */
const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_e) {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  }
};

/**
 * Band Settings → SongShake Feed (ENG-763). Every show has to be streamed to
 * SongShake as well as YouTube/Twitch: SongShake records it and cuts each
 * requested song's clip, and with the server's `requireFeed` on a band can't
 * go live until SongShake is receiving it.
 *
 * Performers of the band get the ingest URL and stream key from
 * `GET /bands/{id}/multistream/stream-key` (the first read creates the
 * band's channel) and can rotate the key. Anyone else is refused with 403
 * and sees no key and no key actions, only the instructions. The key is a
 * secret: it stays in this component's state, masked until revealed, and is
 * never logged or stored.
 *
 * The feed indicator re-reads `GET …/multistream/status` while the view is open.
 */
function MultistreamSetup({ band, onBack }) {
  const bandId = band?.ID ?? band?.id;
  const fetchWrapperRef = useRef(useFetchWrapper());
  const t = useT();
  const { status } = useMultistreamStatus(bandId);

  const [keyData, setKeyData] = useState(null);
  const [loading, setLoading] = useState(true);
  // The stream-key read's failure: `{ key, retryable }`, or null.
  const [loadError, setLoadError] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(null);
  const copyTimerRef = useRef(null);
  const [confirmingRotate, setConfirmingRotate] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [rotateError, setRotateError] = useState(null);
  const [rotated, setRotated] = useState(false);

  const loadKey = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandId}/multistream/stream-key`);
      setKeyData({ ingestUrl: data?.ingestUrl ?? '', streamKey: data?.streamKey ?? '' });
    } catch (err) {
      // Only the status: the response never carries the key on failure, but
      // nothing about this request is logged beyond what failed.
      console.error('Error loading the SongShake stream key:', err?.status, err?.code);
      setKeyData(null);
      setLoadError({ key: streamKeyErrorKey(err), retryable: isRetryableStreamKeyError(err) });
    } finally {
      setLoading(false);
    }
  }, [bandId]);

  useEffect(() => {
    loadKey();
  }, [loadKey]);

  useEffect(() => () => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
  }, []);

  const handleCopy = async (field, text) => {
    if (!text || !(await copyText(text))) return;
    setCopied(field);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(null), COPY_RESET_MS);
  };

  const handleRotate = async () => {
    if (rotating) return;
    setRotating(true);
    setRotateError(null);
    try {
      const data = await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandId}/multistream/stream-key/rotate`);
      setKeyData({ ingestUrl: data?.ingestUrl ?? keyData?.ingestUrl ?? '', streamKey: data?.streamKey ?? '' });
      setConfirmingRotate(false);
      setRevealed(false);
      setRotated(true);
    } catch (err) {
      console.error('Error rotating the SongShake stream key:', err?.status, err?.code);
      setRotateError(t(streamKeyErrorKey(err)));
    } finally {
      setRotating(false);
    }
  };

  return (
    <div className="multistream-setup">
      <div className="band-settings-header">
        <button className="band-settings-back-button" onClick={onBack}>
          <FaArrowLeft className="rtl-mirror" /> {t('common.actions.back')}
        </button>
        <h2 className="band-settings-title">{t('multistream.title')}</h2>
      </div>

      <div className="band-settings-description">
        <p>{t('multistream.description')}</p>
      </div>

      <MultistreamFeedIndicator status={status} />

      {loading ? (
        <LoadingSpinner />
      ) : loadError ? (
        <div className="message error-message" role="alert" data-testid="multistream-key-error">
          <FaTimes className="message-icon" aria-hidden="true" /> {t(loadError.key)}
          {loadError.retryable && (
            <button type="button" className="small-button multistream-retry" onClick={loadKey}>
              {t('common.actions.retry')}
            </button>
          )}
        </div>
      ) : keyData && (
        <div className="multistream-key-panel" data-testid="multistream-key-panel">
          <div className="multistream-field">
            <span className="multistream-field-label" id="multistream-server-label">{t('multistream.key.serverLabel')}</span>
            <code className="multistream-field-value" aria-labelledby="multistream-server-label">{keyData.ingestUrl}</code>
            <button type="button" className="small-button" onClick={() => handleCopy('server', keyData.ingestUrl)}>
              <FaCopy aria-hidden="true" /> {t(copied === 'server' ? 'multistream.key.copied' : 'multistream.key.copyServer')}
            </button>
          </div>
          <div className="multistream-field">
            <span className="multistream-field-label" id="multistream-key-label">{t('multistream.key.keyLabel')}</span>
            <code className="multistream-field-value" aria-labelledby="multistream-key-label" data-testid="multistream-stream-key">
              {revealed ? keyData.streamKey : MASKED_KEY}
            </code>
            <button type="button" className="small-button" onClick={() => setRevealed((r) => !r)} aria-pressed={revealed}>
              {revealed ? <FaEyeSlash aria-hidden="true" /> : <FaEye aria-hidden="true" />}{' '}
              {t(revealed ? 'multistream.key.hide' : 'multistream.key.reveal')}
            </button>
            <button type="button" className="small-button" onClick={() => handleCopy('key', keyData.streamKey)}>
              <FaCopy aria-hidden="true" /> {t(copied === 'key' ? 'multistream.key.copied' : 'multistream.key.copyKey')}
            </button>
          </div>
          <p className="multistream-key-warning">{t('multistream.key.secret')}</p>
          {rotated && (
            <div className="message success-message" role="status">✓ {t('multistream.rotate.done')}</div>
          )}
          <button
            type="button"
            className="remove-button small-button"
            onClick={() => { setRotateError(null); setConfirmingRotate(true); }}
            data-testid="multistream-rotate"
          >
            <FaSyncAlt aria-hidden="true" /> {t('multistream.rotate.action')}
          </button>
        </div>
      )}

      <MultistreamInstructions
        ingestUrl={keyData?.ingestUrl || null}
        streamKey={keyData?.streamKey || null}
        revealed={revealed}
      />

      <ConfirmationModal
        isOpen={confirmingRotate}
        onClose={() => setConfirmingRotate(false)}
        onConfirm={handleRotate}
        title={t('multistream.rotate.title')}
        message={t('multistream.rotate.message')}
        confirmText={t('multistream.rotate.confirm')}
        isDangerous
        confirmTestId="multistream-rotate-confirm"
        error={rotateError}
        isBusy={rotating}
      />
    </div>
  );
}

export default MultistreamSetup;
